import React, { useCallback, useMemo, useState } from 'react';
import type { AiCaptainChoicePrompt } from '../../utils/aiCaptainChoices';

interface CaptainChoiceButtonsProps {
  choice: AiCaptainChoicePrompt;
  disabled?: boolean;
  onSelect: (values: string[]) => void;
}

const CaptainChoiceButtons: React.FC<CaptainChoiceButtonsProps> = ({
  choice,
  disabled = false,
  onSelect,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const isMultiple = choice.selectionMode === 'multiple';

  const minSelections = choice.minSelections ?? 1;
  const maxSelections = choice.maxSelections ?? choice.options.length;

  const selectedValues = useMemo(
    () =>
      choice.options.filter((opt) => selectedIds.has(opt.id)).map((opt) => opt.value),
    [choice.options, selectedIds]
  );

  const handleSingleSelect = useCallback(
    (value: string) => {
      if (disabled) return;
      onSelect([value]);
    },
    [disabled, onSelect]
  );

  const toggleMultiple = useCallback(
    (optionId: string) => {
      if (disabled) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(optionId)) {
          next.delete(optionId);
          return next;
        }
        if (next.size >= maxSelections) return prev;
        next.add(optionId);
        return next;
      });
    },
    [disabled, maxSelections]
  );

  const handleConfirmMultiple = useCallback(() => {
    if (disabled || selectedValues.length < minSelections) return;
    onSelect(selectedValues);
  }, [disabled, minSelections, onSelect, selectedValues]);

  if (isMultiple) {
    return (
      <div
        className="vc-choice-group vc-choice-group--multiple"
        role="group"
        aria-label={choice.prompt}
        data-selection-mode="multiple"
      >
        <div className="vc-choice-group__options vc-choice-group__options--multiple">
          {choice.options.map((option) => {
            const isSelected = selectedIds.has(option.id);
            return (
              <button
                key={option.id}
                type="button"
                className={`vc-choice-btn vc-choice-btn--check${isSelected ? ' vc-choice-btn--selected' : ''}${disabled ? ' vc-choice-btn--disabled' : ''}`}
                disabled={disabled}
                aria-pressed={isSelected}
                onClick={() => toggleMultiple(option.id)}
              >
                <span className="vc-choice-btn__box" aria-hidden />
                {option.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="vc-choice-group__confirm"
          disabled={disabled || selectedValues.length < minSelections}
          onClick={handleConfirmMultiple}
        >
          Confirm selection
        </button>
      </div>
    );
  }

  return (
    <div
      className="vc-choice-group vc-choice-group--single"
      role="radiogroup"
      aria-label={choice.prompt}
      data-selection-mode="single"
    >
      <div className="vc-choice-group__options">
        {choice.options.map((option) => (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={false}
            className={`vc-choice-btn${disabled ? ' vc-choice-btn--disabled' : ''}`}
            disabled={disabled}
            onClick={() => handleSingleSelect(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CaptainChoiceButtons;
